import {Injectable} from '@angular/core';
import {BehaviorSubject, Subject} from 'rxjs';
import {TokenDto} from '../common/dtos/tokenDto';
import {LoggingService} from './logging';
import {Router} from '@angular/router';

/// <summary>
/// Token class to handle JWT tokens, including decoding and checking expiration.
/// </summary>
export class Token {
  private payload: TokenPayload | null = null;
  private token: string | null = null;

  public getPayload(): TokenPayload | null {
    return this.payload;
  }

  public getToken(): string | null {
    return this.token;
  }

  public setToken(token: string | null) {
    if (!token || token.length === 0) {
      this.payload = null;
      this.token = null;
      return;
    }
    this.token = token;
    this.decodeToken(token);
  }

  private decodeToken(token: string) {
    if (!token || token.length === 0) {
      this.payload = null;
      return;
    }
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    this.payload = <TokenPayload>JSON.parse(jsonPayload);
  }

  public isExpired(): boolean {
    if (!this.payload) {
      return true;
    }
    const now = Math.floor(Date.now() / 1000);
    return this.payload.exp < now;
  }

  constructor(private auth: string = '') {
    if (!auth || auth.length === 0) {
      this.payload = null;
    }
    this.decodeToken(this.auth);
  }
}

export interface TokenPayload {
  staffId: string;
  exp: number;
  iat: number;
  iss: string;
  sub: string;
  districtId: string;
  districtName: string;
  firstname: string;
  lastname: string;
  title: string;
  googleId: string;
  roleIds: string[];
  features: string[];
  buildingIds: string[];
  sidrAdmin: boolean;
  roles: string[];
  resetPassword: boolean;
  enabledModules: string[];
}

const JWT_KEY = 'jwt';
const REFRESH_KEY = 'refresh';

@Injectable()
export class TokenService {
  public isLoggedIn: BehaviorSubject<boolean>;
  private tokenDto: Subject<TokenDto>;
  private userToken: Token;
  private refreshToken: Token;

  isAuthenticated(): boolean {
    const value = !this.tokenIsExpired() && !this.refreshIsExpired();
    this.isLoggedIn.next(value);
    return value;
  }

  setToken(token: string | null) {
    if (!token || token.length === 0) {
      window.localStorage.removeItem(JWT_KEY);
      return;
    }
    window.localStorage.setItem(JWT_KEY, token);
    this.userToken.setToken(token);
  }

  setRefresh(token: string | null) {
    if (!token || token.length === 0) {
      window.localStorage.removeItem(REFRESH_KEY);
      return;
    }
    window.localStorage.setItem(REFRESH_KEY, token);
    this.refreshToken.setToken(token);
  }

  getToken(): string | null {
    return this.userToken.getToken();
  }

  getRefresh() {
    return this.refreshToken.getToken();
  }

  getUserToken(): TokenPayload | null {
    return this.userToken.getPayload();
  }

  getRefreshToken(): TokenPayload | null {
    return this.refreshToken.getPayload();
  }

  tokenIsExpired(): boolean {
    return this.userToken.isExpired();
  }

  refreshIsExpired(): boolean {
    return this.refreshToken.isExpired();
  }

  clear() {
    localStorage.removeItem(JWT_KEY);
    localStorage.removeItem(REFRESH_KEY);
    this.userToken.setToken(null);
    this.refreshToken.setToken(null);
    this.isLoggedIn.next(false);
  }

  login() {
    //window.location.href = window.location.protocol + '//' + window.location.hostname + '/login';
  }

  init() {
    this.userToken.setToken(localStorage.getItem(JWT_KEY));
    this.refreshToken.setToken(localStorage.getItem(REFRESH_KEY));

    const isLoggedIn = !this.tokenIsExpired();
    const isRefreshExpired = this.refreshIsExpired();

    if (!isLoggedIn && isRefreshExpired) {
      this.clear();
      return;
    }

    if (!isLoggedIn && isRefreshExpired) {
      this.log.Log(`Token and Refresh are expired`);
      this.isLoggedIn.next(false);
      this.router.navigate(['/']).then(() => this.clear());
    } else
      if (!isLoggedIn && !isRefreshExpired) {
        this.log.Log(`Token is expired but Refresh is not`);
        this.isLoggedIn.next(true);
      } else
        if (isLoggedIn) {
          this.log.Log(`Is logged in`);
          this.isLoggedIn.next(isLoggedIn);
        } else {
          this.log.Log(`Is logged out`);
          this.isLoggedIn.next(isLoggedIn);
        }
  }

  constructor(private log: LoggingService, private router: Router) {
    this.isLoggedIn = new BehaviorSubject<boolean>(false);
    this.tokenDto = new Subject<TokenDto>();
    this.userToken = new Token();
    this.refreshToken = new Token();
    this.init();
  }

}
